/*
 * Time zone control panel directive - Lets users select whether they want to use
 * use the browswer-detected time zone or override it with another one.
 *
 * Angular module: trackerbase-app.views.tracking-calendar-view.time-zone-control-panel.js
 * Directive name: tb-time-zone-control-panel (tbTimeZoneControlPanel)
 *
 */
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectivesModule from "../../shims/kendoDirectives";

import Assert from "tbAssert";

import TimeZoneDataServiceModule from "../../services/TimezoneService";
import CurrentTimeZoneServiceModule from "../../services/CurrentTimeZoneService";
import TimeZoneNameFormatFilterModule from "../../filters/FormatTimeZoneNameFilter";

const mScopeSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mWindowSvc = new WeakMap();
const mTimeZoneSvc = new WeakMap();
const mCurrentTimeZoneSvc = new WeakMap();
const mTZNameFormatFilter = new WeakMap();

const mAdditionalPanelClasses = new WeakMap();
const mPanelIsOpen = new WeakMap();
const mPanelInitialized = new WeakMap();

const mIsUsingDetectedTimeZone = new WeakMap();
const mDetectedTimeZone = new WeakMap();
const mOverrideTimeZone = new WeakMap();

const mAvailableTimeZonesDataSource = new WeakMap();

class TimeZoneControlPanelController {
    constructor($scope,
                $log,
                $window,
                TimeZone,
                CurrentTimeZone,
                tbFormatTZNameFilter)
    {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($window != null, "Assertion Failure: Missing Window Service Dependency");
        Assert.StrictAssert(TimeZone != null, "Assertion Failure: Missing Time Zone Service Dependency");
        Assert.StrictAssert(CurrentTimeZone != null, "Assertion Failure: Missing Current Time Zone Service Dependency");
        Assert.StrictAssert(tbFormatTZNameFilter != null, "Assertion Failure: Missing Time Zone Name Filter Service Dependency");

        mScopeSvc.set(this, $scope);
        mLogSvc.set(this, $log);
        mWindowSvc.set(this, $window);
        mTimeZoneSvc.set(this, TimeZone);
        mCurrentTimeZoneSvc.set(this, CurrentTimeZone);
        mTZNameFormatFilter.set(this, tbFormatTZNameFilter);

        mPanelInitialized.set(this, false);

        mIsUsingDetectedTimeZone.set(this, mCurrentTimeZoneSvc.get(this).isUsingDetectedTimeZone());
        mDetectedTimeZone.set(this, mCurrentTimeZoneSvc.get(this).detectedTimeZone);
        mOverrideTimeZone.set(this, mCurrentTimeZoneSvc.get(this).profileTimeZone);

        let timeZoneDataSourceParams = {
            transport: {
                read: (options) => {
                    mTimeZoneSvc.get(this)
                        .getTimeZones({ countryCode: "US" })
                        .$promise
                        .then(
                            (tzData) => {
                                let preparedTzData = this._prepareAvailableTimeZoneData(tzData);
                                options.success(preparedTzData);
                            },
                            (errorResponse) => {
                                options.error(errorResponse);
                            }
                        );
                }
            },
            schema: {
                model: {
                    id: "zoneId",
                    zoneId: { from: "zoneId" },
                    zoneDisplayName: {from: "zoneDisplayName" }
                }
            }
        };

        mAvailableTimeZonesDataSource.set(this, new kendo.data.DataSource(timeZoneDataSourceParams));

        // FIXME: Apparently redundant line - commented out to see if removal does anything
        // mOverrideTimeZone.set(this, CurrentTimeZone.profileTimeZone);

    }

    get additionalPanelClasses() {
        return mAdditionalPanelClasses.get(this);
    }

    set additionalPanelClasses(updatedVal) {
        mAdditionalPanelClasses.set(this, updatedVal);
    }

    get panelIsOpen() {
        return mPanelIsOpen.get(this);
    }

    set panelIsOpen(updatedVal) {
        mPanelIsOpen.set(this, updatedVal);
    }

    get panelInitialized() {
        return mPanelInitialized.get(this);
    }

    set panelInitialized(updatedVal) {
        mPanelInitialized.set(this, updatedVal);
    }

    get isUsingDetectedTimeZone() {
        return mIsUsingDetectedTimeZone.get(this);
    }

    set isUsingDetectedTimeZone(updatedVal) {
        mIsUsingDetectedTimeZone.set(this, updatedVal);
    }

    get detectedTimeZone() {
        return mDetectedTimeZone.get(this);
    }

    get overrideTimeZone() {
        return mOverrideTimeZone.get(this);
    }

    set overrideTimeZone(updatedVal) {
        mOverrideTimeZone.set(this, updatedVal);
    }

    get availableTimeZonesDataSource() {
        return mAvailableTimeZonesDataSource.get(this);
    }

    // Methods
    _prepareAvailableTimeZoneData(allZones) {

        let preparedZones = allZones.map((tzName) => {
            let tzDisplayName = mTZNameFormatFilter.get(this)(tzName);
            return {
                zoneDisplayName: tzDisplayName,
                zoneId: tzName
            };
        });

        return preparedZones;
    }

    applyTimeZoneSettings(e) {
        if (e != null) {
            e.preventDefault();
        }

        if (this.isUsingDetectedTimeZone === true) {
            mCurrentTimeZoneSvc.get(this).useDetectedTimeZone();
        }
        else {
            mLogSvc.get(this).debug(`Setting ${this.overrideTimeZone}`);
            mCurrentTimeZoneSvc.get(this).profileTimeZone = this.overrideTimeZone;
            mCurrentTimeZoneSvc.get(this).useProfileTimeZone();
        }

        mLogSvc.get(this).debug(mCurrentTimeZoneSvc.get(this));

        mWindowSvc.get(this).location.reload();
    }

    hidePanel() {
        this.panelIsOpen = false;
    }
}

TimeZoneControlPanelController.$inject = [
    "$scope",
    "$log",
    "$window",
    "TimeZone",
    "CurrentTimeZone",
    "tbFormatTZNameFilter"
];

TimeZoneControlPanelController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiBootstrap,
    kendoDirectivesModule,
    TimeZoneDataServiceModule,
    CurrentTimeZoneServiceModule,
    TimeZoneNameFormatFilterModule
];

TimeZoneControlPanelController.template = require("./TimeZoneControlPanel.tpl.html");

TimeZoneControlPanelController.controllerAs = "timeZonePanel";

export default TimeZoneControlPanelController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-calendar-view/TimeZoneControlPanelController.js
// module id = eBJQ
// module chunks = 1